import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { useCatalog } from './CatalogContext'
import { StatusEntry } from '../types/catalog'

type StatusMap = Record<string, string>

type SystemStatusState = {
  statuses: StatusMap
  loading: boolean
  refresh: () => void
}

const SystemStatusContext = createContext<SystemStatusState>({
  statuses: {},
  loading: false,
  refresh: () => {},
})

const resolvePath = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

export function SystemStatusProvider({ children }: { children: React.ReactNode }) {
  const { catalog } = useCatalog()
  const [statuses, setStatuses] = useState<StatusMap>({})
  const [loading, setLoading] = useState(false)

  const fetchStatuses = useCallback(async (entries: StatusEntry[]) => {
    const results: StatusMap = {}

    const processItem = async (item: StatusEntry, parentData: any = null) => {
      if (item.enabled === false) return null

      let currentData = null

      if (item.url) {
        try {
          const res = await fetch(item.url)
          currentData = await res.json()
          // If mapping is NOT provided, use the standard status field
          if (!item.mapping) {
            results[item.name] = currentData.status || (res.ok ? 'Online' : 'Error')
          }
        } catch (e) {
          // Network unreachable (e.g. local dev) is "unknown", not a hard error — don't show red.
          results[item.name] = 'Unknown'
        }
      }

      // If mapping is provided, use it either on currentData (from item.url) or parentData
      const dataToMap = currentData || parentData
      if (item.mapping && dataToMap) {
        const isUp = item.mapping.every((path) => {
          const val = resolvePath(dataToMap, path)
          return val === true || val === 'UP'
        })
        results[item.name] = isUp ? 'Online' : 'Error'
      }

      if (item.children) {
        const childrenStatuses = await traverse(item.children, currentData || parentData)

        // If this item doesn't have its own status yet, but has children
        if (!results[item.name] && childrenStatuses.length > 0) {
          if (childrenStatuses.every((s) => s === 'Online')) {
            results[item.name] = 'Online'
          } else if (childrenStatuses.some((s) => s === 'Error')) {
            results[item.name] = 'Error'
          } else {
            results[item.name] = 'Unknown'
          }
        }
      }

      return results[item.name]
    }

    const traverse = async (items: StatusEntry[], parentData: any = null) => {
      const itemStatuses: string[] = []
      for (const item of items) {
        const s = await processItem(item, parentData)
        if (s) itemStatuses.push(s)
      }
      return itemStatuses
    }

    await traverse(entries)
    return results
  }, [])

  const refresh = useCallback(async () => {
    if (!catalog?.status) return
    setLoading(true)
    const newStatuses = await fetchStatuses(catalog.status)
    setStatuses(newStatuses)
    setLoading(false)
  }, [catalog, fetchStatuses])

  useEffect(() => {
    refresh()
  }, [refresh])

  return (
    <SystemStatusContext.Provider value={{ statuses, loading, refresh }}>
      {children}
    </SystemStatusContext.Provider>
  )
}

export function useSystemStatus() {
  return useContext(SystemStatusContext)
}
